<template>
  <component
    :is="rootTag"
    :href="href"
    class="dp-perk-preview-card d-flex flex-column bg-white w-100 overflow-hidden"
    :class="{ 'dp-perk-preview-card--featured': featured }"
  >
    <div class="perk-media position-relative">
      <img v-if="thumbnailImageUrl" :src="thumbnailImageUrl" :alt="effectiveAltText" class="d-block w-100 h-100" />
      <div v-else class="perk-image-placeholder w-100 h-100" aria-hidden="true" />
      <span
        v-if="thumbnailImageBadgeText"
        class="perk-image-badge position-absolute d-inline-flex align-items-center justify-content-center bg-primary border border-white text-white text-uppercase text-nowrap font-weight-bold"
      >
        {{ thumbnailImageBadgeText }}
      </span>
    </div>
    <div class="perk-content d-flex flex-column flex-grow-1">
      <div class="perk-details d-flex flex-column flex-grow-1">
        <h3 class="perk-header text-primary font-weight-bold m-0">{{ header }}</h3>
        <p class="m-0">{{ subHeader }}</p>
      </div>
      <span
        v-if="footerBadgeText"
        class="perk-footer-badge d-inline-flex align-items-center align-self-start text-uppercase font-weight-bold"
      >
        <svg
          class="perk-footer-icon"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M5.24059 0.0879078L9.56333 1.66048L9.96331 1.80504L9.98831 2.20551C10.0487 3.18226 9.88623 4.67279 9.21126 6.1301C8.53004 7.59914 7.31342 9.06036 5.26559 9.89256L5.00102 10L4.73644 9.89256C2.68445 9.06232 1.46991 7.60305 0.788692 6.13206C0.11372 4.67474 -0.0487731 3.18226 0.011641 2.20746L0.03664 1.80699L0.436623 1.66048L4.75936 0.0879078L4.99893 0L5.24059 0.0879078ZM4.84561 1.72416L4.05939 3.53155C4.03543 3.58644 3.98043 3.62395 3.91731 3.62844L1.84115 3.77105C1.6922 3.78121 1.63178 3.95585 1.74574 4.0463L3.33588 5.30611C3.38421 5.3444 3.40525 5.40516 3.39025 5.46279L2.8934 7.35847C2.85777 7.49443 3.0161 7.60246 3.14318 7.52862L4.91227 6.49971C4.96602 6.46845 5.03414 6.46845 5.08789 6.49971L6.85698 7.52862C6.98385 7.60246 7.14239 7.49443 7.10676 7.35847L6.60991 5.46279C6.5947 5.40516 6.61574 5.3444 6.66428 5.30611L8.25442 4.0463C8.36858 3.95585 8.30796 3.78121 8.15901 3.77105L6.08285 3.62844C6.01972 3.62415 5.96473 3.58664 5.94077 3.53155L5.15455 1.72416C5.0981 1.59445 4.90227 1.59445 4.84602 1.72416H4.84561Z"
            fill="currentColor"
          />
        </svg>
        <span>{{ footerBadgeText }}</span>
      </span>
    </div>
  </component>
</template>

<script>
export default {
  name: 'DpPerkPreviewCard',
  props: {
    thumbnailImageUrl: {
      type: String,
      required: false,
      default: null,
    },
    thumbnailImageBadgeText: {
      type: String,
      required: false,
      default: null,
    },
    thumbnailImageAltText: {
      type: String,
      required: false,
      default: null,
    },
    header: {
      type: String,
      required: true,
    },
    subHeader: {
      type: String,
      required: true,
    },
    footerBadgeText: {
      type: String,
      required: false,
      default: null,
    },
    href: {
      type: String,
      required: false,
      default: null,
    },
    featured: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    rootTag() {
      return this.href ? 'a' : 'article';
    },
    effectiveAltText() {
      return this.thumbnailImageAltText || this.header || '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/_variables';

$perk-card-border: #d8d8d8;
$perk-card-radius: 8px;
$perk-card-premium-bg: #f0eacf;

.dp-perk-preview-card {
  position: relative;
  border-radius: $perk-card-radius;
  color: $dp-gray-7;
  font-size: 16px;
  line-height: 1.5;
  text-decoration: none;

  // Border drawn as a pseudo-element so it sits ON TOP of the image and content
  // areas. `outline` / `box-shadow inset` are painted before child elements in
  // the parent's stacking context, which lets the image cover them at the top
  // edges. An absolutely-positioned ::after layer paints after children and
  // wins reliably.
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    border: 1px solid $perk-card-border;
    border-radius: inherit;
  }

  &:hover,
  &:focus,
  &:focus-visible {
    text-decoration: none !important;
    outline: none;

    &::after {
      border-width: 2px;
      border-color: $blue-800;
    }

    .perk-content {
      background-color: $blue-50;
    }
  }

  &:focus-visible {
    .perk-header {
      text-decoration: underline;
    }
  }

  .perk-media {
    aspect-ratio: 4 / 3;
    background-color: $dp-gray-3;
  }

  .perk-image-placeholder {
    background-color: $dp-gray-3;
  }

  img {
    object-fit: cover;
  }

  .perk-image-badge {
    top: 10px;
    right: 10px;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 14px;
  }

  .perk-content {
    gap: 16px;
    padding: 16px 16px 24px;
  }

  .perk-details {
    gap: 4px;
  }

  .perk-header {
    font-size: 18px;
  }

  .perk-footer-badge {
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    background-color: $perk-card-premium-bg;
    color: $amber-900;
    font-size: 12px;
    line-height: 14px;
  }

  .perk-footer-icon {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
  }

  // Featured layout — horizontal on md+ (image right, content left)
  &.dp-perk-preview-card--featured {
    @media (min-width: 768px) {
      // Bootstrap's flex-column sets flex-direction: column !important, so we must use !important here
      flex-direction: row !important;
      min-height: 300px;

      .perk-media {
        order: 2;
        width: 400px;
        aspect-ratio: unset;
        flex-shrink: 0;
        align-self: stretch;
        border-radius: 0 $perk-card-radius $perk-card-radius 0;

        img {
          height: 100%;
          object-fit: cover;
        }
      }

      .perk-content {
        order: 1;
        flex-grow: 1;
        padding: 24px;
        justify-content: center;
      }

      // Override Bootstrap's flex-grow-1 so details don't expand to fill
      // the full height — allows justify-content: center to actually center
      .perk-details {
        flex-grow: 0 !important;
      }

      .perk-header {
        font-size: 24px;
        line-height: 1.25;
      }
    }
  }
}
</style>
